import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { UserService } from 'src/app/services/user.service';
import {
  ValidatorFn,
  AbstractControlOptions,
  UntypedFormBuilder,
  UntypedFormControl,
} from '@angular/forms';
import { LogService } from 'src/app/services/log.service';
import { DropDown } from 'src/app/interfaces/drop-down.interface';
import { ListViewComponent } from '@syncfusion/ej2-angular-lists';
import { UserDropdownDTO } from 'src/app/interfaces/user-dropdown-list';
import { Subject } from 'rxjs';
import { debounceTime, filter, takeUntil } from 'rxjs/operators';

const MIN_SEARCH_CHARS = 3;
const SEARCH_DEBOUNCE_MS = 300;

@Component({
  selector: 'app-user-form-field',
  templateUrl: './user-form-field.component.html',
  styleUrls: ['./user-form-field.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserFormFieldComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Output() dataLoaded = new EventEmitter<void>();
  @Input() orgId: number;
  @Input() includeChildOrgs: boolean = true;
  @Input() includeGroups: boolean = true;
  @Input() multiSelect: boolean = false;
  @Input() formGroup: any;
  @Input() fieldControlName: any;
  @Input() fieldLabel: any;
  @Input() col: any;
  @Input() validatorOrOpts:
    | ValidatorFn
    | ValidatorFn[]
    | AbstractControlOptions
    | null = null;
  @Input() excludeUsers: number[] = [];
  @ViewChild('textbox') textboxEle: HTMLInputElement;
  @ViewChild('listView') listViewEle: ListViewComponent;

  userList: DropDown<number>[] = [];
  rawData: UserDropdownDTO[] = [];
  selectedOption: DropDown<number> | null = null;
  selectedUserDetailsMap = new Map<number, UserDropdownDTO>();
  private destroy$ = new Subject<void>();
  private searchSubscription: any;

  fields: any = {
    valueField: 'value',
    textField: 'text',
    selectedText: 'selectedText',
  };

  listViewFields: Object = {
    id: 'value',
    text: 'text',
  };

  constructor(
    private _us: UserService,
    private _cdr: ChangeDetectorRef,
    private _logger: LogService,
    private _ufb: UntypedFormBuilder,
  ) {}

  ngAfterViewInit(): void {
    this.setupSingleSelectSearch();
  }

  ngOnChanges() {
    this.initializeData();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getSelectedUserDetails(): UserDropdownDTO[] {
    const ids = this.formGroup?.get(this.fieldControlName)?.value ?? [];
    const idList = Array.isArray(ids) ? ids : [ids];
    return idList
      .map((id: number) => this.selectedUserDetailsMap.get(id))
      .filter(Boolean);
  }

  private initializeData() {
    if (!this.orgId) return;
    if (this.multiSelect) {
      if (this.formGroup?.get(this.fieldControlName) == null) {
        this.formGroup?.addControl(this.fieldControlName, new UntypedFormControl([]));
      }
      this.loading = false;
      this.filteredUserList = [];
      this.userList = [];
      this.rawData = [];
      this._cdr.detectChanges();
    } else {
      this.loading = false;
      this.setupSingleSelectSearch();
    }
  }

  private setupSingleSelectSearch() {
    if (this.multiSelect) return;
    this.searchSubscription?.unsubscribe();
    const ctrl = this.formGroup?.get(this.fieldControlName);
    if (!ctrl) return;
    this.searchSubscription = ctrl.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        filter((v) => typeof v === 'string' || typeof v === 'number'),
        debounceTime(SEARCH_DEBOUNCE_MS),
      )
      .subscribe((value) => {
        if (typeof value === 'number') {
          const opt = this.userList.find((o) => o.value === value);
          if (opt) this.selectedOption = opt;
          return;
        }
        const str = value == null ? '' : String(value);
        if (str.trim() === '') this.selectedOption = null;
        const term = str.trim();
        if (term.length >= MIN_SEARCH_CHARS && this.orgId != null) {
          this.loading = true;
          this._cdr.detectChanges();
          this._us
            .getUserDropdownList(this.orgId, this.includeChildOrgs, this.includeGroups, term)
            .subscribe((data) => {
              const { list } = this.mapData(data ?? []);
              this.userList = this.selectedOption ? [this.selectedOption, ...list] : list;
              this.rawData = data ?? [];
              this.loading = false;
              this._cdr.detectChanges();
              this.dataLoaded.emit();
            });
        } else {
          this.userList = this.selectedOption ? [this.selectedOption] : [];
          this.loading = false;
          this._cdr.detectChanges();
        }
      });
  }

  private mapData(data: UserDropdownDTO[]): { list: DropDown<number>[]; raw: UserDropdownDTO[] } {
    const raw = data.filter(
      (usr) =>
        usr.isGroup === false &&
        usr.hasWebAccess === true &&
        !this.excludeUsers.includes(usr.id),
    );
    const list = raw
      .map((usr) => ({
        value: usr.id,
        text: `${usr.userName ? usr.userName + ', ' : ''} ${usr.firstName} ${usr.lastName}`.trim(),
        ['selectedText']: usr.name,
      }))
      .sort((a, b) => a.text.localeCompare(b.text));
    return { list, raw };
  }

  filteredUserList: DropDown<number>[] = [];
  loading = true;

  onFilterKeyUp($event: { target: { value: string } }) {
    const value = ($event.target?.value ?? '').trim();
    if (value.length < MIN_SEARCH_CHARS) {
      this.filteredUserList = [];
      this.rawData = [];
      this.loading = false;
      this._cdr.detectChanges();
      return;
    }
    if (this.orgId == null) return;
    this.loading = true;
    this._cdr.detectChanges();
    this._us
      .getUserDropdownList(this.orgId, this.includeChildOrgs, this.includeGroups, value)
      .subscribe((data) => {
        const { list, raw } = this.mapData(data ?? []);
        this.rawData = raw;
        this.filteredUserList = list;
        this.userList = list;
        this.loading = false;
        this._cdr.detectChanges();
        this.dataLoaded.emit();
      });
  }

  onSelect($event: {
    isInteracted: boolean;
    isChecked: boolean;
    data: { value: number; text: string };
  }) {
    if (!$event.isInteracted) return;
    const selectedValue = $event.data.value;
    const dto = this.rawData.find((u) => u.id === selectedValue);
    if ($event.isChecked && dto) this.selectedUserDetailsMap.set(selectedValue, dto);
    else this.selectedUserDetailsMap.delete(selectedValue);

    let currentValue = this.formGroup.get(this.fieldControlName)?.value ?? [];
    if ($event.isChecked) {
      currentValue = [...currentValue, selectedValue];
    } else {
      currentValue = currentValue.filter((id: number) => id !== selectedValue);
    }
    this.formGroup.get(this.fieldControlName).setValue(currentValue);
  }

  clearSelection(): void {
    this.formGroup?.get(this.fieldControlName)?.setValue(this.multiSelect ? [] : null);
    this.selectedUserDetailsMap.clear();
    this.selectedOption = null;
    this._cdr.detectChanges();
  }
}
